import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { ScrollContainer } from "@/components/ui/scroll-container";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/utils/time";
import { faviconUrl, extractDomainFromUrl } from "@/utils/url";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { RotateCwIcon } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { AGENT_TYPE_REGISTRY, UNKNOWN_AGENT_META } from "./agent-registry";
import {
  buildChainOfThoughtState,
  mergeAdjacentSteps,
} from "./chain-of-thought-state";
import { CountBadge } from "./count-badge";
import { SourcesFaviconRow } from "./sources-favicon-row";
import { StepLabel } from "./step-label";
import type { SearchReasoningProps } from "./types";

const AUTO_CLOSE_DELAY = 3000;
const MS_IN_S = 1000;
const MAX_HEADER_FAVICONS = 3;

const SearchReasoningComponent = ({
  reasoningEvents,
  isStreaming,
  className,
  rightContent,
  searchEngineResult,
  companiesCount,
  employeesCount,
  query,
  onReplay,
}: SearchReasoningProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useControllableState({ defaultProp: true });
  const [hasAutoClosed, setHasAutoClosed] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);

  const state = useMemo(
    () =>
      buildChainOfThoughtState(
        reasoningEvents,
        searchEngineResult?.searchEngineAgents ?? [],
        isStreaming,
      ),
    [reasoningEvents, searchEngineResult?.searchEngineAgents, isStreaming],
  );

  const steps = useMemo(
    () =>
      mergeAdjacentSteps(state.steps, (step) => {
        const meta = AGENT_TYPE_REGISTRY[step.agentType] ?? UNKNOWN_AGENT_META;
        return `${step.agentType}|${meta.getDescription?.(step.agent) ?? ""}`;
      }),
    [state.steps],
  );

  const totalCompanies = useMemo(
    () => steps.reduce((sum, s) => sum + s.companies.length, 0),
    [steps],
  );
  const allSources = useMemo(
    () => steps.flatMap((s) => s.sources),
    [steps],
  );

  useEffect(() => {
    if (isStreaming) {
      if (startTime === null) {
        setStartTime(Date.now());
        setHasAutoClosed(false);
        setIsOpen(true);
      }
    } else if (startTime !== null) {
      setDuration(Math.ceil((Date.now() - startTime) / MS_IN_S));
      setStartTime(null);
    }
  }, [isStreaming, startTime, setIsOpen]);

  useEffect(() => {
    if (
      !isStreaming &&
      isOpen &&
      !hasAutoClosed &&
      reasoningEvents.length > 0
    ) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setHasAutoClosed(true);
      }, AUTO_CLOSE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, isOpen, hasAutoClosed, reasoningEvents, setIsOpen]);

  if (reasoningEvents.length === 0 && !isStreaming) {
    return null;
  }

  const activeStep = steps.find((s) => s.status === "active");
  const activeLabel = activeStep
    ? t(
        (AGENT_TYPE_REGISTRY[activeStep.agentType] ?? UNKNOWN_AGENT_META)
          .labelKey,
      )
    : t("prospecting.search.reasoning.agents.orchestrator");

  const completedLabel = t("prospecting.search.reasoning.searchCompleted");

  const headerSummary = (
    <span className="inline-flex flex-wrap gap-2 items-center">
      {isStreaming ? (
        <Shimmer className="text-sm font-medium text-foreground">
          {activeLabel}
        </Shimmer>
      ) : (
        <span className="font-medium text-foreground">{completedLabel}</span>
      )}
      <span className="text-muted-foreground/40">&middot;</span>

      <span>
        {(employeesCount ?? 0) > 0 ||
        steps.some((s) => s.agentType === "employee")
          ? t("prospecting.search.reasoning.employeesFound", {
              count: employeesCount ?? 0,
            })
          : t("prospecting.search.reasoning.companiesFound", {
              count: companiesCount ?? totalCompanies,
            })}
      </span>

      {duration !== undefined && !isStreaming && (
        <>
          <span className="text-muted-foreground/40">&middot;</span>
          <span>{formatDuration(duration)}</span>
        </>
      )}

      {allSources.length > 0 && (
        <>
          <span className="text-muted-foreground/40">&middot;</span>
          <span className="inline-flex items-center gap-1.5">
            <span>
              {t("prospecting.search.reasoning.sources", {
                count: allSources.length,
              })}
            </span>
            <span className="inline-flex items-center gap-0.5">
              {allSources.slice(0, MAX_HEADER_FAVICONS).map((s, i) => (
                <img
                  key={i}
                  src={faviconUrl(
                    s.url ? extractDomainFromUrl(s.url) : s.title,
                  )}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="size-3.5 rounded-sm"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ))}
              {allSources.length > MAX_HEADER_FAVICONS && (
                <span className="ml-0.5 text-[10px] text-muted-foreground/60">
                  +{allSources.length - MAX_HEADER_FAVICONS}
                </span>
              )}
            </span>
          </span>
        </>
      )}

    </span>
  );

  const isQueryStepActive =
    isStreaming && steps.every((s) => s.status === "pending");
  const showCompletion = !isStreaming && reasoningEvents.length > 0;

  return (
    <div className={cn("mx-auto mt-6 w-full max-w-4xl", className)}>
      <ChainOfThought open={isOpen} onOpenChange={setIsOpen} defaultOpen={true}>
        <ChainOfThoughtHeader isLoading={isStreaming}>
          {headerSummary}
        </ChainOfThoughtHeader>

        <ChainOfThoughtContent>
          <ScrollContainer
            ref={contentRef}
            autoScroll={isStreaming}
            className="max-h-60"
          >
            <ChainOfThoughtStep
              label={
                <StepLabel
                  label={t("prospecting.search.reasoning.queryAnalysis")}
                  isActive={isQueryStepActive}
                />
              }
              description={query}
              status={isQueryStepActive ? "active" : "complete"}
              isLast={steps.length === 0 && !showCompletion}
            />

            {steps.map((step, index) => {
              const meta =
                AGENT_TYPE_REGISTRY[step.agentType] ?? UNKNOWN_AGENT_META;
              const description =
                step.sources.length > 0
                  ? t("prospecting.search.reasoning.sourcesScanned", {
                      count: step.sources.length,
                    })
                  : meta.getDescription?.(step.agent);

              return (
                <ChainOfThoughtStep
                  key={step.id}
                  label={
                    <StepLabel
                      label={t(meta.labelKey)}
                      isActive={step.status === "active"}
                    />
                  }
                  description={description}
                  status={step.status}
                  isLast={index === steps.length - 1 && !showCompletion}
                >
                  <SourcesFaviconRow sources={step.sources} />
                  {step.agentType === "employee" ? (
                    <CountBadge
                      count={employeesCount ?? step.agent.resultsCount}
                      variant="employee"
                    />
                  ) : (
                    <CountBadge count={step.companies.length} />
                  )}
                </ChainOfThoughtStep>
              );
            })}

            {showCompletion && (
              <ChainOfThoughtStep
                label={
                  <span className="font-medium text-foreground">
                    {duration !== undefined
                      ? t(
                          "prospecting.search.reasoning.searchCompletedDuration",
                          { duration: formatDuration(duration) },
                        )
                      : t("prospecting.search.reasoning.searchCompleted")}
                  </span>
                }
                status="complete"
                isLast
              />
            )}
          </ScrollContainer>
        </ChainOfThoughtContent>

        {!isStreaming && (rightContent || onReplay) && (
          <div className="flex gap-2 justify-between items-center mt-3">
            <div className="flex gap-1 items-center">{rightContent}</div>
            {onReplay && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReplay}
                className="gap-1.5"
              >
                <RotateCwIcon className="size-3.5" />
                {t("prospecting.search.reasoning.replay")}
              </Button>
            )}
          </div>
        )}
      </ChainOfThought>
    </div>
  );
};

export const SearchReasoning = memo(SearchReasoningComponent);
SearchReasoning.displayName = "SearchReasoning";
